<template>
  <section class="motion-section" :class="{ 'agency-motion': agency, 'motion-paused': paused }">
    <div class="motion-inner">
      <header class="motion-header">
        <h2 class="motion-title">{{ agency ? "Show the work. Bill the work." : tr("The product moves the way you do.") }}</h2>
        <p class="motion-sub">{{ agency ? "Track the hours behind an engagement. Give clients one place to review files and invoices." : tr("Bookings, billable hours and client approvals, straight from the app.") }}</p>
      </header>

      <div class="motion-grid" tabindex="0">
        <!-- 1 · Online booking → team calendar -->
        <article v-if="!agency" class="motion-card">
          <div ref="stage0" class="motion-stage" aria-hidden="true">
            <div class="mb-page" :class="{ off: t[0] > 3.5 }">
              <div class="mb-head">
                <span class="mb-logo">SN</span>
                <span><b>Discovery call</b><small>Studio North · 30 min · video</small></span>
              </div>
              <div class="mb-days">
                <span v-for="d in bookingDays" :key="d[0]" :class="{ on: d[2] }">
                  <small>{{ d[0] }}</small><b>{{ d[1] }}</b>
                </span>
              </div>
              <div class="mb-slots">
                <span v-for="s in bookingSlots" :key="s" :class="{ on: s === '10:00' && t[0] > 1.75 }">{{ s }}</span>
              </div>
              <span class="mb-confirm" :class="{ ready: t[0] > 1.8, press: t[0] > 2.55 && t[0] < 2.75, done: t[0] > 2.75 }">
                <span class="mi">{{ t[0] > 2.75 ? "check" : "event_available" }}</span>
                {{ t[0] > 2.75 ? "Booked for Tue 14, 10:00" : "Confirm Tue 14, 10:00" }}
              </span>
              <span class="m-cursor" :class="cursorClass(t[0], 0.7, 1.75, 2.55, 3.3)" :style="{ '--x': cursorX(t[0]), '--y': cursorY(t[0]) }">
                <span class="mi">near_me</span><i>Maya</i>
              </span>
            </div>

            <div class="mb-cal" :class="{ on: t[0] > 3.5 }">
              <div class="mb-cal-head">
                <b>Team calendar</b><small>Oct 13 – 17</small>
              </div>
              <div class="mb-grid">
                <span class="mb-gutter"><i>9</i><i>10</i><i>11</i><i>12</i></span>
                <div v-for="(d, di) in calDays" :key="d" class="mb-col">
                  <small>{{ d }}</small>
                  <div class="mb-col-body">
                    <span
                      v-for="ev in calEvents.filter((e) => e.day === di)"
                      :key="ev.name"
                      class="mb-ev"
                      :class="[ev.tone, { new: ev.isNew, in: !ev.isNew || t[0] > 3.95 }]"
                      :style="{ top: `${ev.at * 25}%`, height: `${ev.len * 25}%` }"
                    >{{ ev.name }}</span>
                  </div>
                </div>
              </div>
              <span class="mb-reminder" :class="{ on: t[0] > 4.8 }">
                <span class="mi">notifications_active</span>Email and SMS reminders scheduled
              </span>
            </div>
          </div>
          <div class="motion-caption">
            <span class="motion-icon"><span class="mi tiny">event_available</span></span>
            <div>
              <h3>{{ tr("Clients book themselves") }}</h3>
              <p>{{ tr("Share a booking page. The time they pick lands on your team calendar, with reminders scheduled.") }}</p>
            </div>
          </div>
        </article>

        <!-- 2 · Tracked time → invoice lines -->
        <article class="motion-card">
          <div ref="stage1" class="motion-stage" aria-hidden="true">
            <div class="mt-wrap">
              <div class="mt-head">
                <span><b>Brand &amp; website</b><small>Forma Studio · unbilled time</small></span>
                <span class="mt-timer"><i></i>{{ timerLabel }}</span>
              </div>
              <ul class="mt-rows">
                <li v-for="(row, ri) in timeRows" :key="row[0]" :class="{ on: t[1] > 0.9 + ri * 0.4 }">
                  <span class="mt-check"><span class="mi">check</span></span>
                  <span class="mt-name">{{ row[0] }}<small>{{ row[1] }}</small></span>
                  <b>{{ row[2] }}h</b>
                  <em :class="{ billed: t[1] > 4.2 }">{{ t[1] > 4.2 ? "Billed" : "Unbilled" }}</em>
                </li>
              </ul>
              <div class="mt-bar">
                <span>{{ selectedCount }} selected · {{ selectedHours }}h</span>
                <span class="mt-btn" :class="{ ready: t[1] > 1.8, press: t[1] > 2.45 && t[1] < 2.65 }">
                  <span class="mi">receipt_long</span>Add to invoice
                </span>
              </div>
              <span class="m-cursor plain" :class="cursorClass(t[1], 1.6, 2.3, 2.45, 2.95)" style="--x: 78%; --y: 86%">
                <span class="mi">near_me</span>
              </span>
            </div>

            <div class="mt-sheet" :class="{ on: t[1] > 2.85 }">
              <div class="mt-sheet-head">
                <b>Invoice draft</b><small>INV-1043 · Forma Studio</small>
              </div>
              <div v-for="(row, ri) in timeRows" :key="row[0]" class="mt-line" :class="{ on: t[1] > 3.25 + ri * 0.35 }">
                <span>{{ row[0] }}</span>
                <small>{{ row[2] }}h × $120</small>
                <b>${{ (row[2] * 120).toFixed(2) }}</b>
              </div>
              <div class="mt-total">
                <span>Total</span>
                <b>${{ invoiceTotal }}</b>
              </div>
            </div>
          </div>
          <div class="motion-caption">
            <span class="motion-icon"><span class="mi tiny">timer</span></span>
            <div>
              <h3>{{ tr("Hours turn into invoice lines") }}</h3><small v-if="agency" class="agency-plan">Time tracking on every plan</small>
              <p>{{ tr("Track time against the project, select the unbilled hours, and they land on the invoice.") }}</p>
            </div>
          </div>
        </article>

        <!-- 3 · Client portal: approve a file, pay an invoice -->
        <article class="motion-card">
          <div ref="stage2" class="motion-stage" aria-hidden="true">
            <div class="mp-portal">
              <div class="mp-top">
                <span class="mb-logo">SN</span>
                <span><b>Studio North</b><small>Client portal · Maya Chen</small></span>
              </div>
              <div class="mp-project">
                <span><b>Brand &amp; website</b><small>Due Nov 14</small></span>
                <span class="mp-progress"><i :style="{ width: (t[2] > 2.1 ? 72 : 64) + '%' }"></i></span>
                <em>{{ t[2] > 2.1 ? 72 : 64 }}%</em>
              </div>
              <div class="mp-row">
                <span class="mp-ic file"><span class="mi">image</span></span>
                <span class="mp-tx"><b>homepage-v2.png</b><small>Shared by Alex</small></span>
                <span v-if="t[2] <= 1.95" class="mp-btn" :class="{ press: t[2] > 1.75 }">Approve</span>
                <span v-else class="mp-chip ok"><span class="mi">check</span>Approved</span>
              </div>
              <div class="mp-row">
                <span class="mp-ic inv"><span class="mi">receipt_long</span></span>
                <span class="mp-tx"><b>INV-1042</b><small>$4,800.00 · due Oct 18</small></span>
                <span v-if="t[2] <= 4.55" class="mp-btn pay" :class="{ press: t[2] > 4.35 }">Pay now</span>
                <span v-else class="mp-chip ok"><span class="mi">check</span>Paid</span>
              </div>
              <span class="m-cursor" :class="mpCursorClass" :style="{ '--x': mpCursor[0], '--y': mpCursor[1] }">
                <span class="mi">near_me</span><i>Maya</i>
              </span>
            </div>

            <div class="mp-toast" :class="{ on: (t[2] > 2.2 && t[2] < 3.9) || t[2] > 4.8 }">
              <span class="mi">{{ t[2] > 4.8 ? "receipt_long" : "task_alt" }}</span>
              <span v-if="t[2] > 4.8"><b>🎉 Invoice #1042 paid in full</b><small>$4800.00 from Forma Studio</small></span>
              <span v-else><b>Maya approved homepage-v2.png</b><small>Brand &amp; website · just now</small></span>
            </div>
          </div>
          <div class="motion-caption">
            <span class="motion-icon"><span class="mi tiny">web</span></span>
            <div>
              <h3>{{ tr("A portal clients actually use") }}</h3><small v-if="agency" class="agency-plan">Illustrative client approval and payment</small>
              <p>{{ tr("Clients approve files and pay invoices in one place, and your team sees it as it happens.") }}</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
const { tr } = useHomeI18n();
const props = defineProps({ agency: { type: Boolean, default: false }, paused: { type: Boolean, default: false } });

// Three loops, one clock each (seconds into the loop). Template logic reads
// the clock, so every state is deterministic, pauses offscreen, and shows a
// representative frame under reduced motion. All stages are fixed height.
const LOOPS = [8, 8.5, 8];
const STILL = [4.6, 5.6, 5.4];

const bookingDays = [
  ["Mon", 13, false],
  ["Tue", 14, true],
  ["Wed", 15, false],
  ["Thu", 16, false],
  ["Fri", 17, false],
];
const bookingSlots = ["9:00", "10:00", "11:30", "13:00", "14:30", "16:00"];
const calDays = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const calEvents = [
  { day: 0, at: 0, len: 1, name: "Standup", tone: "blue" },
  { day: 2, at: 2, len: 1.4, name: "Design review", tone: "purple" },
  { day: 3, at: 0.5, len: 1, name: "Kickoff", tone: "green" },
  { day: 4, at: 2.5, len: 1, name: "Handoff", tone: "amber" },
  { day: 1, at: 1, len: 1, name: "Discovery · Maya Chen", tone: "brand", isNew: true },
];
const timeRows = [
  ["Homepage design", "Alex · Mon", 2.5],
  ["Content migration", "Sam · Tue", 1.75],
  ["Design review", "Alex · Wed", 1],
];

const stage0 = ref(null);
const stage1 = ref(null);
const stage2 = ref(null);
const t = ref([0, 0, 0]);
const visible = [false, false, false];

const ease = (x) => 1 - Math.pow(1 - Math.min(Math.max(x, 0), 1), 3);
const lerp = (a, b, k) => a + (b - a) * ease(k);

// Cursor helpers: travel in from the corner, click, then leave.
const cursorClass = (time, arrive, at, click, leave) => ({
  show: time > arrive && time < leave,
  click: time > click && time < click + 0.25,
});
// Waypoints: [time, x%, y%]; eased between neighbours.
const along = (time, points, axis) => {
  if (time <= points[0][0]) return `${points[0][axis]}%`;
  for (let i = 1; i < points.length; i++) {
    if (time <= points[i][0]) {
      const [t0] = points[i - 1];
      const k = (time - t0) / (points[i][0] - t0);
      return `${lerp(points[i - 1][axis], points[i][axis], k)}%`;
    }
  }
  return `${points[points.length - 1][axis]}%`;
};
// Booking: in from the corner → the 10:00 slot → Confirm.
const bookingPath = [
  [0.7, 92, 98],
  [1.6, 52, 43],
  [1.85, 52, 43],
  [2.4, 58, 77],
];
const cursorX = (time) => along(time, bookingPath, 1);
const cursorY = (time) => along(time, bookingPath, 2);

const selectedCount = computed(() => timeRows.filter((_, i) => t.value[1] > 0.9 + i * 0.4).length);
const selectedHours = computed(() =>
  timeRows.filter((_, i) => t.value[1] > 0.9 + i * 0.4).reduce((a, r) => a + r[2], 0).toFixed(2).replace(/\.00$/, ""),
);
const invoiceTotal = computed(() => {
  const k = ease((t.value[1] - 4) / 1);
  return (630 * k).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
});
const timerLabel = computed(() => {
  const secs = 42 * 60 + 15 + Math.floor(t.value[1]);
  return `00:${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
});

// Portal: Approve on the file row, then Pay now on the invoice row.
const portalPath = [
  [0.8, 94, 100],
  [1.7, 80, 61],
  [3.3, 80, 61],
  [4.2, 80, 79],
];
const mpCursor = computed(() => [along(t.value[2], portalPath, 1), along(t.value[2], portalPath, 2)]);
const mpCursorClass = computed(() => {
  const time = t.value[2];
  return {
    show: time > 0.8 && time < 5.2,
    click: (time > 1.75 && time < 2) || (time > 4.35 && time < 4.6),
  };
});

let timer = null;
let last = 0;
let io = null;

onMounted(() => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) {
    t.value = [...STILL];
    return;
  }
  const stages = [stage0.value, stage1.value, stage2.value];
  io = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        visible[stages.indexOf(e.target)] = e.isIntersecting;
      }),
    { threshold: 0.2 },
  );
  stages.forEach((st) => st && io.observe(st));
  last = performance.now();
  timer = window.setInterval(() => {
    const now = performance.now();
    const dt = Math.min(0.25, (now - last) / 1000);
    last = now;
    if (document.hidden || props.paused) return;
    const next = t.value.map((v, i) => (visible[i] ? (v + dt) % LOOPS[i] : v));
    t.value = next;
  }, 80);
});

onBeforeUnmount(() => {
  io?.disconnect();
  if (timer) window.clearInterval(timer);
});
</script>

<style scoped>
.agency-motion .motion-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.agency-plan { display: block; font-size: 11px; color: var(--muted); margin: 8px 0; }
.motion-paused :deep(*) { animation-play-state: paused !important; }
@media(max-width: 760px) { .agency-motion .motion-grid { grid-template-columns: 1fr; } }

.motion-section {
  --line: #e3e9ee;
  --surface: #fff;
  --canvas: #f7f8fa;
  --ink: #171717;
  --muted: #595f68;
  --brand: #0e7490;
  --ok: #2d7a4e;
  --ok-fill: #e6f4ea;
  /* A step off white so it doesn't merge with the lifecycle section above. */
  background: #f8fafc;
  padding: 70px 0 80px;
}

.dark .motion-section {
  --line: #1e293b;
  --surface: #111a2b;
  --canvas: #0b1420;
  --ink: #f1f5f9;
  --muted: #94a3b8;
  --ok: #6ed39a;
  --ok-fill: rgb(63 178 110 / 0.18);
  background: #020617;
}

.motion-inner {
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1rem;
}

@media (min-width: 640px) {
  .motion-inner {
    padding: 0 1.5rem;
  }
}

.motion-header {
  max-width: 660px;
  margin: 0 auto;
  text-align: center;
}

.motion-title {
  margin: 0;
  color: #0f172a;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -1px;
}

.dark .motion-title {
  color: #fff;
}

.motion-sub {
  margin-top: 12px;
  color: #5c6b76;
  font-size: 14px;
  line-height: 1.8;
}

.dark .motion-sub {
  color: #94a3b8;
}

.motion-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  margin-top: 34px;
}

.motion-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
  transition:
    transform 0.3s cubic-bezier(0.2, 0.7, 0.3, 1),
    box-shadow 0.3s;
}

.motion-card:hover {
  box-shadow: 0 14px 34px rgb(20 40 60 / 0.08);
  transform: translateY(-3px);
}

.motion-stage {
  position: relative;
  height: 270px;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #fbfcfd, #f2f6f8);
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  text-align: left;
}

.dark .motion-stage {
  background: linear-gradient(180deg, #101a2b, #0b1420);
}

.motion-caption {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 16px 18px;
}

.motion-icon {
  display: grid;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 7px;
  background: #e8f6fb;
  color: #0e7490;
}

.dark .motion-icon {
  background: rgb(27 167 206 / 0.14);
  color: #67e8f9;
}

.motion-caption h3 {
  margin: 2px 0 5px;
  text-wrap: pretty; /* no one-word last line on narrow phones */
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.dark .motion-caption h3 {
  color: #fff;
}

.motion-caption p {
  margin: 0;
  color: #5c6b76;
  font-size: 12px;
  line-height: 1.65;
}

.dark .motion-caption p {
  color: #94a3b8;
}

.motion-section .mi {
  font-family: "Material Icons Outlined";
  font-size: 15px;
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  white-space: nowrap;
  direction: ltr;
  font-feature-settings: "liga";
}

.motion-section .mi.tiny {
  font-size: 15px;
}

/* ---------- shared bits ---------- */
.mb-logo {
  display: grid;
  width: 30px;
  height: 30px;
  flex: none;
  place-items: center;
  border-radius: 8px;
  background: #171717;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.dark .mb-logo {
  background: #f1f5f9;
  color: #0f172a;
}

.m-cursor {
  position: absolute;
  z-index: 6;
  left: var(--x);
  top: var(--y);
  color: #7948af;
  opacity: 0;
  pointer-events: none;
  transition:
    left 0.12s linear,
    top 0.12s linear,
    opacity 0.25s;
}

.m-cursor.plain {
  color: #171717;
}

.dark .m-cursor.plain {
  color: #fff;
}

.m-cursor.show {
  opacity: 1;
}

.m-cursor .mi {
  display: block;
  font-size: 19px;
  filter: drop-shadow(0 2px 3px rgb(0 0 0 / 0.25));
  transform: rotate(-90deg) scaleX(-1);
}

.m-cursor i {
  position: absolute;
  top: 15px;
  left: 13px;
  border-radius: 5px;
  background: #7948af;
  color: #fff;
  font-size: 9.5px;
  font-style: normal;
  font-weight: 600;
  padding: 2px 6px;
}

.m-cursor.click::after {
  position: absolute;
  top: -9px;
  left: -9px;
  width: 20px;
  height: 20px;
  border: 2px solid #1ba7ce;
  border-radius: 50%;
  animation: mRipple 0.4s ease-out both;
  content: "";
}

@keyframes mRipple {
  from {
    opacity: 1;
    transform: scale(0.4);
  }

  to {
    opacity: 0;
    transform: scale(1.6);
  }
}

/* ---------- 1 · booking ---------- */
.mb-page,
.mb-cal {
  position: absolute;
  inset: 0;
  padding: 16px;
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
}

.mb-page.off {
  opacity: 0;
  transform: translateY(-10px);
}

.mb-cal {
  opacity: 0;
  transform: translateY(12px);
}

.mb-cal.on {
  opacity: 1;
  transform: none;
}

.mb-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mb-head b,
.mp-top b {
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.mb-head small,
.mp-top small {
  display: block;
  color: var(--muted);
  font-size: 10.5px;
}

.mb-days {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  margin-top: 14px;
}

.mb-days span {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  padding: 5px 0;
}

.mb-days small {
  color: var(--muted);
  font-size: 9.5px;
}

.mb-days b {
  font-size: 13px;
  font-weight: 600;
}

.mb-days span.on {
  border-color: #171717;
  background: #171717;
  color: #fff;
}

.mb-days span.on small {
  color: #cfcfcf;
}

.dark .mb-days span.on {
  border-color: #f1f5f9;
  background: #f1f5f9;
  color: #0f172a;
}

.mb-slots {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 10px;
}

.mb-slots span {
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--surface);
  font-size: 11.5px;
  font-weight: 500;
  padding: 6px 0;
  text-align: center;
  transition:
    background 0.25s,
    border-color 0.25s,
    color 0.25s;
}

.mb-slots span.on {
  border-color: var(--brand);
  background: rgb(27 167 206 / 0.12);
  color: #0e7490;
}

.dark .mb-slots span.on {
  color: #67e8f9;
}

.mb-confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  border-radius: 8px;
  background: #e5e7eb;
  color: #526171;
  font-size: 12px;
  font-weight: 600;
  padding: 9px 0;
  transition:
    background 0.3s,
    color 0.3s,
    transform 0.15s;
}

.dark .mb-confirm {
  background: #1e293b;
  color: #64748b;
}

.mb-confirm.ready {
  background: #171717;
  color: #fff;
}

.dark .mb-confirm.ready {
  background: #f1f5f9;
  color: #0f172a;
}

.mb-confirm.press {
  transform: scale(0.96);
}

.mb-confirm.done {
  background: var(--ok);
  color: #fff;
}

.mb-cal-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.mb-cal-head b {
  font-size: 13px;
  font-weight: 600;
}

.mb-cal-head small {
  color: var(--muted);
  font-size: 10.5px;
}

.mb-grid {
  display: grid;
  grid-template-columns: 18px repeat(5, minmax(0, 1fr));
  gap: 4px;
  margin-top: 10px;
}

.mb-gutter {
  display: grid;
  grid-template-rows: repeat(4, 37px);
  margin-top: 18px;
  color: var(--muted);
  font-size: 9px;
}

.mb-gutter i {
  font-style: normal;
}

.mb-col small {
  display: block;
  color: var(--muted);
  font-size: 9.5px;
  text-align: center;
}

.mb-col-body {
  position: relative;
  height: 148px;
  margin-top: 4px;
  border-radius: 6px;
  background:
    repeating-linear-gradient(180deg, transparent 0 36px, var(--line) 36px 37px),
    var(--surface);
}

.mb-ev {
  position: absolute;
  right: 2px;
  left: 2px;
  overflow: hidden;
  border-left: 3px solid var(--tone);
  border-radius: 4px;
  background: var(--fill);
  color: var(--ink);
  font-size: 8.5px;
  font-weight: 600;
  line-height: 1.2;
  padding: 3px 3px 3px 4px;
}

.mb-ev.blue { --tone: #4a7de0; --fill: #ebf0fd; }
.mb-ev.purple { --tone: #8b5cf6; --fill: #f4ecfc; }
.mb-ev.green { --tone: #2d9d63; --fill: #e6f4ea; }
.mb-ev.amber { --tone: #f0a832; --fill: #fdf4c3; }
.mb-ev.brand { --tone: #1ba7ce; --fill: #dff3f9; }

.dark .mb-ev {
  --fill: #1b2638;
}

.mb-ev.new {
  opacity: 0;
  transform: translateY(-14px) scale(0.9);
  transition:
    opacity 0.35s,
    transform 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.3);
}

.mb-ev.new.in {
  box-shadow: 0 0 0 3px rgb(27 167 206 / 0.18);
  opacity: 1;
  transform: none;
}

.mb-reminder {
  position: absolute;
  right: 16px;
  bottom: 14px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  background: var(--ok-fill);
  color: var(--ok);
  font-size: 11px;
  font-weight: 600;
  opacity: 0;
  padding: 7px 10px;
  transform: translateY(8px);
  transition:
    opacity 0.3s,
    transform 0.35s;
}

.mb-reminder.on {
  opacity: 1;
  transform: none;
}

/* ---------- 2 · time → invoice ---------- */
.mt-wrap {
  position: absolute;
  inset: 0;
  padding: 16px;
}

.mt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mt-head b {
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.mt-head small {
  display: block;
  color: var(--muted);
  font-size: 10.5px;
}

.mt-timer {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  background: #fdecec;
  color: #ba3a37;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  padding: 3px 8px;
}

.dark .mt-timer {
  background: rgb(229 83 78 / 0.18);
  color: #f69591;
}

.mt-timer i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: mBlink 1.2s ease-in-out infinite;
}

@keyframes mBlink {
  50% {
    opacity: 0.25;
  }
}

.mt-rows {
  display: grid;
  gap: 6px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.mt-rows li {
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px 10px;
  transition: border-color 0.25s;
}

.mt-rows li.on {
  border-color: rgb(27 167 206 / 0.45);
}

.mt-check {
  display: grid;
  width: 16px;
  height: 16px;
  flex: none;
  place-items: center;
  border: 1.5px solid #c9c9c9;
  border-radius: 4px;
  color: transparent;
  transition: all 0.25s;
}

.mt-check .mi {
  font-size: 12px;
}

.on .mt-check {
  border-color: var(--brand);
  background: var(--brand);
  color: #fff;
}

.mt-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mt-name small {
  margin-left: 6px;
  color: var(--muted);
  font-size: 10px;
}

.mt-rows b {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.mt-rows em {
  width: 54px;
  flex: none;
  border-radius: 20px;
  background: #fdf4c3;
  color: #946000;
  font-size: 9.5px;
  font-style: normal;
  font-weight: 600;
  padding: 2px 0;
  text-align: center;
}

.dark .mt-rows em {
  background: rgb(224 170 36 / 0.17);
  color: #f1c95e;
}

.mt-rows em.billed,
.dark .mt-rows em.billed {
  background: var(--ok-fill);
  color: var(--ok);
}

.mt-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  color: var(--muted);
  font-size: 11px;
}

.mt-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 7px;
  background: #e5e7eb;
  color: #526171;
  font-size: 11.5px;
  font-weight: 600;
  padding: 7px 11px;
  transition:
    background 0.3s,
    color 0.3s,
    transform 0.15s;
}

.dark .mt-btn {
  background: #1e293b;
  color: #64748b;
}

.mt-btn.ready {
  background: #171717;
  color: #fff;
}

.dark .mt-btn.ready {
  background: #f1f5f9;
  color: #0f172a;
}

.mt-btn.press {
  transform: scale(0.95);
}

.mt-sheet {
  position: absolute;
  z-index: 4;
  right: 10px;
  bottom: 0;
  left: 10px;
  height: 184px;
  border: 1px solid var(--line);
  border-bottom: 0;
  border-radius: 12px 12px 0 0;
  background: var(--surface);
  box-shadow: 0 -14px 34px rgb(15 23 42 / 0.14);
  padding: 13px 14px;
  transform: translateY(105%);
  transition: transform 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.05);
}

.mt-sheet.on {
  transform: none;
}

.mt-sheet-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px solid var(--line);
  padding-bottom: 8px;
}

.mt-sheet-head b {
  font-size: 13px;
  font-weight: 600;
}

.mt-sheet-head small {
  color: var(--muted);
  font-size: 10.5px;
}

.mt-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 64px;
  align-items: baseline;
  gap: 8px;
  border-bottom: 1px dashed var(--line);
  font-size: 11.5px;
  opacity: 0;
  padding: 6px 0;
  transform: translateX(-8px);
  transition:
    opacity 0.3s,
    transform 0.3s;
}

.mt-line.on {
  opacity: 1;
  transform: none;
}

.mt-line small {
  color: var(--muted);
  font-size: 10px;
}

.mt-line b,
.mt-total b {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: right;
}

.mt-total {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
}

.mt-total b {
  font-size: 17px;
}

/* ---------- 3 · portal ---------- */
.mp-portal {
  position: absolute;
  inset: 0;
  padding: 16px;
}

.mp-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mp-project {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px 30px;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: var(--surface);
  padding: 10px 11px;
}

.mp-project b,
.mp-tx b {
  display: block;
  font-size: 12px;
  font-weight: 600;
}

.mp-project small,
.mp-tx small {
  display: block;
  color: var(--muted);
  font-size: 10px;
}

.mp-progress {
  height: 5px;
  overflow: hidden;
  border-radius: 5px;
  background: var(--canvas);
}

.mp-progress i {
  display: block;
  height: 100%;
  border-radius: 5px;
  background: linear-gradient(90deg, #1ba7ce, #2d7a4e);
  transition: width 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.mp-project em {
  font-size: 11px;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: right;
}

.mp-row {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 8px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: var(--surface);
  padding: 9px 10px;
}

.mp-ic {
  display: grid;
  width: 28px;
  height: 28px;
  flex: none;
  place-items: center;
  border-radius: 7px;
}

.mp-ic.file {
  background: #f4ecfc;
  color: #7948af;
}

.mp-ic.inv {
  background: #ebf0fd;
  color: #3f58bf;
}

.dark .mp-ic.file {
  background: rgb(154 111 224 / 0.2);
  color: #bea5f2;
}

.dark .mp-ic.inv {
  background: rgb(91 127 234 / 0.2);
  color: #93aef9;
}

.mp-tx {
  min-width: 0;
  flex: 1;
}

.mp-btn {
  flex: none;
  border-radius: 7px;
  background: #171717;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 11px;
  transition: transform 0.15s;
}

.dark .mp-btn {
  background: #f1f5f9;
  color: #0f172a;
}

.mp-btn.pay {
  background: #0e7490;
  color: #fff;
}

.mp-btn.press {
  transform: scale(0.94);
}

.mp-chip {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 3px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 4px 9px;
  animation: mPop 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.4) both;
}

.mp-chip.ok {
  background: var(--ok-fill);
  color: var(--ok);
}

.mp-chip .mi {
  font-size: 13px;
}

@keyframes mPop {
  from {
    opacity: 0;
    transform: scale(0.7);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* Lands at the top, like the app's foreground notification. */
.mp-toast {
  position: absolute;
  z-index: 5;
  top: 10px;
  right: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 11px;
  background: #111;
  box-shadow: 0 16px 36px rgb(0 0 0 / 0.28);
  color: #fff;
  opacity: 0;
  padding: 10px 12px;
  transform: translateY(-14px);
  transition:
    opacity 0.3s,
    transform 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.mp-toast.on {
  opacity: 1;
  transform: none;
}

.mp-toast > .mi {
  display: grid;
  width: 28px;
  height: 28px;
  flex: none;
  place-items: center;
  border-radius: 7px;
  background: #262626;
  font-size: 16px;
}

.mp-toast b {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
}

.mp-toast small {
  display: block;
  color: #b5b5b5;
  font-size: 10px;
}

/* ---------- layout ---------- */
@media (max-width: 960px) {
  .motion-grid {
    grid-template-columns: minmax(0, 1fr);
    max-width: 30rem;
    margin-right: auto;
    margin-left: auto;
  }
}

/* Phones: one swipe row (each loop plays when it is on screen) instead of a two-screen stack. */
@media (max-width: 640px) {
  .motion-section {
    padding: 48px 0 52px;
  }

  .motion-title {
    font-size: 30px;
    letter-spacing: -0.6px;
  }

  .motion-grid {
    display: flex;
    max-width: none;
    gap: 12px;
    margin: 26px -1rem 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding: 0 1rem 6px;
    scroll-padding-inline: 1rem;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .motion-grid::-webkit-scrollbar {
    display: none;
  }

  .motion-card {
    flex: 0 0 86%;
    scroll-snap-align: start;
  }

  .motion-card:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .motion-stage *,
  .motion-stage *::after {
    animation: none !important;
    transition: none !important;
  }
}

/* Muted text needs its own foreground on the dark demo surfaces. */
</style>
